import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/motion";
import { useParallax } from "@/lib/useParallax";
import { Target, Globe, Atom } from "lucide-react";

const PILLARS = [
  {
    icon: Target,
    title: "Energy Independence",
    desc: "Accelerate the shift from imported fossil dependency to domestically manufactured, high-density lithium-ion energy systems capable of powering critical infrastructure at national scale.",
  },
  {
    icon: Globe,
    title: "Scalable Manufacturing",
    desc: "Establish world-class production capacity that delivers consistent output quality across GWh-scale volumes, meeting the demands of OEMs, utilities, and government programs simultaneously.",
  },
  {
    icon: Atom,
    title: "Technology-Led Growth",
    desc: "Invest in advanced cell chemistry research, next-generation BMS architectures, and AI-driven quality systems that compound our manufacturing advantage over each production cycle.",
  },
];

export default function VisionSection() {
  const { ref, y } = useParallax(0.25);
  return (
    <section
      ref={ref}
      data-testid="vision-section"
      className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay overflow-hidden"
    >
      {/* Decorative parallax orb */}
      <motion.div
        style={{ y }}
        aria-hidden
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#1E96FC]/8 blur-3xl pointer-events-none will-change-transform"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mb-4"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // Our Vision
          </span>
        </motion.div>

        <div className="mb-6 md:mb-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportConfig}
            variants={fadeInUp}
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-white leading-tight mb-4 md:mb-5">
              <span className="block">Building energy infrastructure</span>
              <span className="block text-[#1E96FC]">that tomorrow demands</span>
            </h2>
            <p className="text-slate-400 leading-relaxed text-sm max-w-2xl">
              Our vision extends beyond battery manufacturing. We're engineering 
              the foundational systems that will enable India's transition to a 
              clean, electrified, and self-reliant energy economy.
            </p>
          </motion.div>
        </div>

        {/* Pillars */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
        >
          {PILLARS.map((p, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              className="glass-card rounded-sm p-6 md:p-8 group transition-all duration-300 hover:bg-[#003771]/20"
            >
              <p.icon className="w-6 h-6 text-[#1E96FC] mb-3" />
              <h3 className="text-xl font-bold uppercase text-slate-100 mb-3 tracking-wide">
                {p.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {p.desc}
              </p>
              <div className="mt-6 h-px bg-gradient-to-r from-[#1E96FC]/30 to-transparent" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
